import { InfoIconPopover, InfoPopover, Typography } from '@automata/ui';
import { Button, MenuItem, Popover } from '@mui/material';
import { MouseEvent, useCallback, useState } from 'react';
import { useToggle } from 'usehooks-ts';

import { NewParameterReferenceModal } from './NewParameterReferenceModal';
import { ParameterType } from './types';
import { allParameterTypes, createParameter, getParameterIcon } from './utils';

interface NewParameterSelectorProps {
  showHint?: boolean;
}

export const NewParameterSelector = ({
  showHint,
}: NewParameterSelectorProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [newParameterType, setNewParameterType] =
    useState<ParameterType | null>(null);
  const [showNewParameterReferenceModal, handleNewParameterReferenceModal] =
    useToggle();

  const parameter = newParameterType ? createParameter(newParameterType) : null;

  const handleClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => setAnchorEl(null), []);

  const handleParameterSelect = useCallback(
    (type: ParameterType) => {
      setNewParameterType(type);
      handleNewParameterReferenceModal();
      handleClose();
    },
    [handleClose, handleNewParameterReferenceModal]
  );

  return (
    <>
      <Button id="new-parameter" variant="contained" onClick={handleClick}>
        New parameter reference
      </Button>
      {showHint && (
        <InfoIconPopover
          infoPopover={
            <InfoPopover
              title="Parameter reference"
              copy="Parameter references can be applied across your workflow and be overridden at runtime. <br/><br/> To apply a parameter reference in nodes properties - click on the parameter reference icon next to the required field label and select a parameter reference from the list."
            />
          }
          infoPopoverAnchorOrigin={{
            vertical: 'center',
            horizontal: 'right',
          }}
          infoPopoverTransformOrigin={{
            vertical: 'center',
            horizontal: 'left',
          }}
        />
      )}

      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        onClose={handleClose}
      >
        {allParameterTypes.map((type) => (
          <MenuItem
            key={type}
            sx={{ width: '240px' }}
            onClick={() => handleParameterSelect(type)}
          >
            {getParameterIcon(type)}
            <Typography marginLeft={1} variant="body2">
              {type}
            </Typography>
          </MenuItem>
        ))}
      </Popover>
      {showNewParameterReferenceModal && parameter && (
        <NewParameterReferenceModal
          parameter={parameter}
          typeOptions={allParameterTypes}
          onClose={handleNewParameterReferenceModal}
        />
      )}
    </>
  );
};
